import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, centavos, formatMoney, hoy, newId, parseMoney, type CuentaBancoCordobas } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { conCuentasPuestas, cuentaEnCordobas, nombreDeCuenta } from '../lib/bancos.js';
import { porPagar, type PorPagar } from '../lib/cuadrar.js';
import { emitirYVer } from '../lib/emitir.js';
import { useT } from '../i18n/index.js';
import { Respuestas, type Paso } from './Asistente.js';
import { Campo } from './Campo.js';
import { Dato, DatoTexto, fechaCorta } from './CuentasDeCaja.js';
import { ElegirCuenta } from './ElegirCuenta.js';

/**
 * A payment to a supplier, a question to a screen: which debt, how much and on what day, how it was
 * paid, the transfer's number, and a check (`compra.pagada`, `gasto.pagado`). What «Gastos» does
 * with «Abonar», asked the way the till asks for a count. Cash comes out of the safe, so one paid
 * this week and not written down is asked for before the safe is counted.
 */

type Forma = 'efectivo_cordobas' | 'transferencia' | 'billetera';

export const PASOS_DE_PAGO = ['pago-deuda', 'pago-monto', 'pago-forma', 'pago-referencia', 'pago-revise'] as const;

export function usePasosDePago({ ir, alGuardar }: { ir: (id: string) => void; alGuardar: (eventoId: string) => void }): {
  pasos: Paso[];
  /** From the first question, or with the debt already chosen. */
  empezar: (deuda?: PorPagar) => void;
} {
  const t = useT();
  const datos = useLiveQuery(
    async () => {
      const [compras, gastos, proveedores] = await Promise.all([db.compras.toArray(), db.gastos.toArray(), db.proveedores.toArray()]);
      return { deudas: porPagar(compras, gastos), proveedores };
    },
    [],
    { deudas: [], proveedores: [] },
  );
  const [deuda, setDeuda] = useState<PorPagar | null>(null);
  const [texto, setTexto] = useState('');
  const [dia, setDia] = useState('');
  const [forma, setForma] = useState<Forma | null>(null);
  const [referencia, setReferencia] = useState('');
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** By transfer, the córdoba account it came from, once picked. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBancoCordobas | null>(null);
  const cuenta = forma === 'transferencia' ? cuentaEnCordobas(config, cuentaElegida) : null;
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nombre = (d: PorPagar) => datos.proveedores.find((p) => p.id === d.proveedorId)?.nombre ?? t('gastos.nadieEnParticular');
  const monto = parseMoney(texto);
  const fecha = dia || hoy();
  const montoValido = deuda !== null && monto !== null && monto > 0 && deuda.pendiente >= monto;
  const conReferencia = forma === 'transferencia' || forma === 'billetera';
  const valido = montoValido && forma !== null && fecha <= hoy();

  function elegir(d: PorPagar) {
    setDeuda(d);
    setTexto((d.pendiente / 100).toFixed(2));
    setError(null);
    ir('pago-monto');
  }

  async function guardar() {
    if (!valido || !deuda) return;
    setGuardando(true);
    setError(null);
    try {
      const pago = {
        fecha,
        monto: monto!,
        formaPago: forma!,
        ...(cuenta ? { cuenta } : {}),
        ...(conReferencia && referencia.trim() ? { referencia: referencia.trim() } : {}),
        // Named, so it can be voided if it was never made.
        pagoId: newId(),
      };
      const { id, rechazo } =
        deuda.tipo === 'compra'
          ? await emitirYVer('compra.pagada', { compraId: deuda.id, ...pago })
          : await emitirYVer('gasto.pagado', { gastoId: deuda.id, ...pago });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setDeuda(null);
      setTexto('');
      setDia('');
      setForma(null);
      setReferencia('');
      setCuentaElegida(null);
      alGuardar(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  const comoSePago =
    forma === 'efectivo_cordobas'
      ? t('pagoPaso.efectivo')
      : forma === 'transferencia'
        ? t('gastoPaso.transferencia')
        : forma === 'billetera'
          ? t('gastoPaso.billetera')
          : '';
  const ids = PASOS_DE_PAGO.filter((id) => id !== 'pago-referencia' || conReferencia);

  const todos: Record<(typeof PASOS_DE_PAGO)[number], Paso> = {
    'pago-deuda': {
      id: 'pago-deuda',
      titulo: t('pagoPaso.deuda'),
      explicacion: <p>{t('pagoPaso.deudaExplicacion')}</p>,
      contenido:
        datos.deudas.length === 0 ? (
          <p className="tarjeta p-3 text-sm text-slate-400">{t('pagoPaso.nadaPorPagar')}</p>
        ) : (
          <Respuestas
            opciones={datos.deudas.map((d) => ({
              clave: d.id,
              etiqueta: nombre(d),
              detalle: [
                d.detalle,
                t('pagoPaso.debe', { monto: formatMoney(d.pendiente) }),
                ...(d.vence ? [t('pagoPaso.vence', { fecha: fechaCorta(d.vence) })] : []),
              ]
                .filter(Boolean)
                .join(' · '),
              icono: d.tipo === 'compra' ? 'compras' : 'gastos',
              elegida: deuda?.id === d.id,
              alTocar: () => elegir(d),
            }))}
          />
        ),
    },
    'pago-monto': {
      id: 'pago-monto',
      titulo: t('pagoPaso.monto', { nombre: deuda ? nombre(deuda) : '' }),
      explicacion: <p>{t('pagoPaso.montoExplicacion', { monto: formatMoney(deuda?.pendiente ?? centavos(0)) })}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo
            etiqueta={t('gastos.montoCordobas')}
            inputMode="decimal"
            className="tabular text-lg"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="0.00"
            autoComplete="off"
            error={texto.trim() !== '' && !montoValido ? t('pagoPaso.montoInvalido') : null}
          />
          <Campo
            etiqueta={t('pagoPaso.fecha')}
            type="date"
            value={fecha}
            max={hoy()}
            onChange={(e) => setDia(e.target.value)}
            error={fecha <= hoy() ? null : t('gastoPaso.fechaFutura')}
          />
        </div>
      ),
      seguir: { etiqueta: t('asistente.siguiente'), deshabilitado: !montoValido || fecha > hoy(), alTocar: () => ir('pago-forma') },
    },
    'pago-forma': {
      id: 'pago-forma',
      titulo: t('pagoPaso.forma'),
      explicacion: <p>{t('pagoPaso.formaExplicacion')}</p>,
      contenido: (
        <Respuestas
          opciones={(
            [
              ['efectivo_cordobas', 'pagoPaso.efectivo', 'pagoPaso.efectivoDetalle', 'cobrar'],
              ['transferencia', 'gastoPaso.transferencia', 'gastoPaso.transferenciaDetalle', 'transferencias'],
              ['billetera', 'gastoPaso.billetera', 'gastoPaso.billeteraDetalle', 'billetera'],
            ] as const
          ).map(([f, etiqueta, detalle, icono]) => ({
            etiqueta: t(etiqueta),
            detalle: t(detalle),
            icono,
            elegida: forma === f,
            alTocar: () => {
              setForma(f);
              ir(f === 'efectivo_cordobas' ? 'pago-revise' : 'pago-referencia');
            },
          }))}
        />
      ),
    },
    'pago-referencia': {
      id: 'pago-referencia',
      titulo: t('gastoPaso.referencia'),
      explicacion: <p>{t('gastoPaso.referenciaExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <Campo etiqueta={t('comun.referencia')} value={referencia} onChange={(e) => setReferencia(e.target.value)} autoComplete="off" maxLength={80} />
          {cuenta && <ElegirCuenta valor={cuenta} onCambiar={(c) => setCuentaElegida(c as CuentaBancoCordobas)} etiqueta={t('bancos.pagaDe')} />}
        </div>
      ),
      seguir: { etiqueta: t('asistente.siguiente'), alTocar: () => ir('pago-revise') },
    },
    'pago-revise': {
      id: 'pago-revise',
      titulo: t('turno.revise'),
      explicacion: <p>{t('pagoPaso.reviseExplicacion')}</p>,
      contenido: (
        <div className="flex flex-col gap-4">
          <div className="tarjeta flex flex-col gap-2">
            {deuda && <DatoTexto etiqueta={t('gastos.proveedor')} valor={nombre(deuda)} />}
            {deuda?.detalle && <DatoTexto etiqueta={t('pagoPaso.de')} valor={deuda.detalle} />}
            <Dato etiqueta={t('pagoPaso.cuanto')} valor={monto !== null ? formatMoney(monto) : ''} />
            <Dato etiqueta={t('cuadrar.fecha')} valor={fechaCorta(fecha)} />
            <DatoTexto etiqueta={t('gastos.formaPago')} valor={comoSePago} />
            {cuenta && conCuentasPuestas(config) && <DatoTexto etiqueta={t('bancos.pagaDe')} valor={nombreDeCuenta(config, cuenta, t)} />}
            {conReferencia && referencia.trim() && <DatoTexto etiqueta={t('comun.referencia')} valor={referencia.trim()} />}
          </div>
          {error && <p className="text-sm text-rose-400">{error}</p>}
        </div>
      ),
      seguir: { etiqueta: t('pagoPaso.guardar'), icono: 'guardar', deshabilitado: guardando || !valido, alTocar: () => void guardar() },
    },
  };

  return {
    pasos: ids.map((id) => todos[id]),
    empezar: (d) => {
      setError(null);
      setForma(null);
      setReferencia('');
      setDia('');
      if (d) elegir(d);
      else {
        setDeuda(null);
        setTexto('');
        ir('pago-deuda');
      }
    },
  };
}
